import { useMemo } from 'react';
import {
  HubElementContentType,
  HubElementPlacement,
  useLightningOutDataQuery,
} from '../__generated__/graphql';

type HubElementDef = ReturnType<
  typeof useLightningOutDataQuery
>['data'] extends
  | { hubElementDefinitions: ReadonlyArray<infer T> }
  | null
  | undefined
  ? T
  : never;

export type HubElement = NonNullable<HubElementDef>;

export type UseHubElementsResult = {
  loading: boolean;
  sidebar: HubElement[];
  profileDropdown: HubElement[];
  dashboard: HubElement[];
  getGroupChildren: (parentId: string) => HubElement[];
  byId: Map<string, HubElement>;
};

const sortBySortOrder = (a: HubElement, b: HubElement): number =>
  a.config.sortOrder - b.config.sortOrder;

export const useHubElements = (): UseHubElementsResult => {
  const { data, loading } = useLightningOutDataQuery({
    fetchPolicy: 'cache-first',
  });

  return useMemo(() => {
    const all: HubElement[] = data?.hubElementDefinitions
      ? [...data.hubElementDefinitions]
      : [];

    const byId = new Map<string, HubElement>();
    const childrenByParent = new Map<string, HubElement[]>();
    for (const el of all) {
      byId.set(el.Id, el);
      const parentId = el.flair__Parent_Element__c;
      if (parentId) {
        const list = childrenByParent.get(parentId) ?? [];
        list.push(el);
        childrenByParent.set(parentId, list);
      }
    }

    const topLevel = all.filter((el) => !el.flair__Parent_Element__c);

    const byPlacement = (placement: HubElementPlacement) =>
      topLevel
        .filter((el) => el.config.placement === placement)
        .sort(sortBySortOrder);

    const getGroupChildren = (parentId: string): HubElement[] => {
      const children = childrenByParent.get(parentId);
      if (!children) {
        return [];
      }
      return [...children].sort(sortBySortOrder);
    };

    return {
      loading,
      sidebar: byPlacement(HubElementPlacement.Sidebar),
      profileDropdown: byPlacement(HubElementPlacement.ProfileDropdown),
      dashboard: byPlacement(HubElementPlacement.Dashboard),
      getGroupChildren,
      byId,
    };
  }, [data, loading]);
};

export { HubElementContentType, HubElementPlacement };
